/* eslint-disable no-restricted-imports */
// JWeb Initializer: Handles plugin registration and readiness state management for JWeb
// This module ensures that JWeb and its plugins are loaded and ready before use.

import { jWebReady } from '@jarvis/jweb-core';
import { registerValueStorePlugin } from '@jarvis/web-value-store';
import { registerEventServicePlugin } from '@jarvis/web-event-service';
import { registerDataCollectionPlugin } from '@jarvis/web-data-collection';
import { JWebWindow } from './types';

// List of plugin keys for type safety and iteration
const pluginKeys = [
  'Auth',
  'Console',
  'Container',
  'DataCollection',
  'Device',
  'EventService',
  'ServiceRouting',
  'ValueStore'
] as const;

// Type representing valid plugin keys
// Used for type-safe access to plugin state objects
// e.g., PluginKey = 'ValueStore' | 'EventService' | ...
type PluginKey = (typeof pluginKeys)[number];

// State object for each plugin/library
// Holds resolve/reject functions for readiness promises
// Used to control when each plugin's promise is resolved or rejected
// 'resolve' is called when the plugin is ready, 'reject' on failure
type PluginState = {
  resolve: ((value: boolean) => void) | undefined;
  reject: ((reason?: unknown) => void) | undefined;
};

// Stores the resolve/reject functions for each readiness promise
// This allows us to resolve/reject the promises from anywhere in the code
let jWebResultStates = {
  JWebLibrary: {
    resolve: undefined,
    reject: undefined
  } as PluginState,
  defaultPluginsRegistered: {
    resolve: undefined,
    reject: undefined
  } as PluginState,
  plugins: pluginKeys.reduce((acc, key) => {
    acc[key] = {
      resolve: undefined,
      reject: undefined
    } as PluginState;
    return acc;
  }, {} as Record<PluginKey, PluginState>)
};
let jWebStates = {
  JWebLibrary: Promise.resolve(true),
  defaultPluginsRegistered: Promise.resolve(true),
  plugins: pluginKeys.reduce((acc, key) => {
    acc[key] = new Promise<boolean>((resolve, reject) => {
      jWebResultStates.plugins[key] = { resolve, reject };
    });
    return acc;
  }, {} as Record<PluginKey, Promise<boolean>>)
};

// This sets up the initial state of the promises that will be used to track readiness
// Each plugin will have its own promise that resolves when the plugin is ready
const _initializeJWebResultStates = (): void => {
  jWebResultStates = {
    JWebLibrary: {
      resolve: undefined,
      reject: undefined
    } as PluginState,
    defaultPluginsRegistered: {
      resolve: undefined,
      reject: undefined
    } as PluginState,
    plugins: pluginKeys.reduce((acc, key) => {
      acc[key] = {
        resolve: undefined,
        reject: undefined
      } as PluginState;
      return acc;
    }, {} as Record<PluginKey, PluginState>)
  };

  // Promises that represent the readiness state of JWeb and its plugins
  // These are exported for other modules to await on
  jWebStates = {
    // Resolves when the JWeb library is loaded
    JWebLibrary: new Promise<boolean>((resolve, reject) => {
      jWebResultStates.JWebLibrary = { resolve, reject };
    }),
    // Resolves when the default plugins are registered
    defaultPluginsRegistered: new Promise<boolean>((resolve, reject) => {
      jWebResultStates.defaultPluginsRegistered = { resolve, reject };
    }),
    // Resolves when each individual plugin is ready
    plugins: pluginKeys.reduce((acc, key) => {
      acc[key] = new Promise<boolean>((resolve, reject) => {
        jWebResultStates.plugins[key] = { resolve, reject };
      });
      return acc;
    }, {} as Record<PluginKey, Promise<boolean>>)
  };
};
_initializeJWebResultStates();

// Initializes JWeb and manages readiness state
// Registers plugins, waits for JWeb to be ready, then unlocks plugin promises
const initializingJWebAndStates = async (): Promise<void> => {
  registerOnceJwebPlugins();
  return jWebReady
    .then((jweb) => {
      if (!jweb) {
        // If jWeb is not defined, reject all promises
        _rejectAllPromises();
      }

      // JWeb library loaded
      if (jweb) {
        console?.debug?.('JShell: jWebReady fully loaded.');
        jWebResultStates.JWebLibrary.resolve?.(true);
        if (jweb.Plugins) {
          jWebResultStates.defaultPluginsRegistered.resolve?.(true);
          unlockingPlugins();
          return;
        }

        // If plugins are not found, reject the default plugins promise
        _rejectDefaultPluginsRegistered();
      }
    })
    .catch((error) => {
      // JWeb failed to load
      console?.error?.(`jWebReady: unable to load JWEB (error: ${error})`);
      _rejectAllPromises();
    });
};

const _rejectDefaultPluginsRegistered = (): void => {
  console?.error?.('JWeb error: Plugins not found in JWeb instance.');
  jWebResultStates.defaultPluginsRegistered.reject?.(false);
  unlockingPlugins();
};

const _rejectAllPromises = (): void => {
  console?.error?.('JWeb error: JWeb instance was not found.');
  jWebResultStates.JWebLibrary.reject?.(false);
  jWebResultStates.defaultPluginsRegistered.reject?.(false);
  unlockingPlugins();
};

// Registers all default JWeb plugins (ValueStore, DataCollection, EventService)
// Should only be called once
const registerOnceJwebPlugins = (): void => {
  registerEventServicePlugin();
  registerDataCollectionPlugin();
  registerValueStorePlugin();
  console?.debug?.('JShell: called the JWeb plugins registers.');
};

// Checks if each plugin is available on window.JWeb.Plugins
// Resolves or rejects the corresponding plugin promise
const unlockingPlugins = (): void => {
  const jWebPlugins = (window as unknown as JWebWindow)?.JWeb?.Plugins;
  pluginKeys.forEach((key) => {
    if (jWebPlugins?.[key]) {
      jWebResultStates.plugins[key].resolve?.(true);
    } else {
      jWebResultStates.plugins[key].reject?.(
        'the plugin ' + key + ' is not available'
      );
    }
  });
};

// Export readiness promises and registration/init functions
export {
  jWebReady, // Promise that resolves when JWeb is ready
  jWebStates, // Object containing all readiness promises
  registerOnceJwebPlugins, // Function to register plugins (usually not needed externally)
  _initializeJWebResultStates, // Function to reset the state (only for Testing)
  unlockingPlugins, // Function to check and resolve plugin readiness (only for Testing)
  initializingJWebAndStates // Function to re-initialize (usually not needed externally)
};
